import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from "react";
import "./styles.css";
import api from "../../../../../services/api";
import EmpresaContext from "../../../../../contexts/Empresa";
import ScriptsKanban from "./ScriptsKanban";
import { ConectarPlayneeConversasModal } from "../../../../security/Users/me/components/Canais/modals/ConectarPlayneeConversasModal";

const LIMIT = 30;
const MAX_DOM_CARDS = 50;

// ---------------------------------------------------------------------------
function normalizar(str) {
  return (str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function extrairCriterios(oportunidade) {
  const criterios = new Set();
  if (oportunidade?.estagio) criterios.add(normalizar(oportunidade.estagio));
  if (oportunidade?.campanha) criterios.add(normalizar(oportunidade.campanha));
  if (oportunidade?.anuncio) criterios.add(normalizar(oportunidade.anuncio));
  if (oportunidade?.tags && typeof oportunidade.tags === "string") {
    oportunidade.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .forEach((t) => criterios.add(normalizar(t)));
  }
  criterios.delete("");
  return [...criterios];
}

export function aplicarVariaveis(texto, oportunidade) {
  const hoje = new Date();
  const amanha = new Date(hoje);
  amanha.setDate(amanha.getDate() + 1);
  const formatarData = (d) =>
    d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  return texto
    .replace(/\[Nome\]/gi, oportunidade?.title || "[Nome]")
    .replace(/\[Cliente\]/gi, oportunidade?.title || "[Cliente]")
    .replace(/\[Vendedor\]/gi, oportunidade?.user?.name || oportunidade?.colaborador?.name || "[Vendedor]")
    .replace(/\[Produto\]/gi, oportunidade?.produto || oportunidade?.produtos?.[0]?.nome || "[Produto]")
    .replace(/\[Empresa\]/gi, oportunidade?.campanha || oportunidade?.anuncio || "[Empresa]")
    .replace(/\[Campanha\]/gi, oportunidade?.campanha || "[Campanha]")
    .replace(/\[Anuncio\]/gi, oportunidade?.anuncio || "[Anúncio]")
    .replace(/\[data\]/gi, formatarData(amanha));
}

function calcularScore(script, criteriosAtivos) {
  if (criteriosAtivos.length === 0) return 0;
  return criteriosAtivos.reduce((acc, c) => {
    return acc + script.criterios.filter((sc) => sc.includes(c) || c.includes(sc)).length;
  }, 0);
}

function formatarScripts(data) {
  return (data || []).map((s) => ({
    key: String(s.id),
    titulo: s.titulo,
    descricao: s.descricao || "",
    texto: s.conteudo || "",
    criterios: s.tags ? s.tags.split(",").map((t) => normalizar(t)) : [],
    empresa_id: s.empresa_id || s.empresaId || null,
  }));
}

// ---------------------------------------------------------------------------
const ScriptCard = React.memo(({ script, criteriosAtivos, adicionarCriterio, oportunidade, onAction, temCanalWhatsapp }) => {
  const textoOriginal = useMemo(
    () => aplicarVariaveis(script.texto, oportunidade),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [script.key, oportunidade?.id]
  );

  const telefoneCliente = useMemo(() => {
    if (Array.isArray(oportunidade?.telefones) && oportunidade.telefones.length > 0) {
      const t = oportunidade.telefones[0];
      const num = t.ddd ? `${t.ddd}${t.numero}` : t.numero;
      return /\d{7,}/.test(num) ? num : "";
    }
    const raw = oportunidade?.telefone || "";
    const first = String(raw).split(",")[0].trim();
    return /\d{7,}/.test(first) ? first : "";
  }, [oportunidade?.telefone, oportunidade?.telefones]);

  return (
    <div className="sm-card">
      {script.score >= 2 && <span className="sm-card-badge">Alta Relevância</span>}
      <div style={{ marginBottom: "8px" }}>
        <p className="sm-card-title" style={{ margin: 0, marginBottom: script.descricao ? "2px" : "8px" }}>
          {script.titulo}
        </p>
        {script.descricao && (
          <p style={{ margin: 0, fontSize: "11px", color: "#888", lineHeight: "1.3" }}>
            {script.descricao}
          </p>
        )}
      </div>

      <div className="sm-card-text" style={{ marginBottom: "12px", whiteSpace: "pre-wrap" }}>
        {textoOriginal}
      </div>

      <div className="sm-card-tags">
        {script.empresa_id && (
          <span
            className="sm-list-tag"
            style={{
              background: "#ecfdf5",
              color: "#059669",
              borderColor: "#a7f3d0",
              cursor: "default",
              fontWeight: "700",
            }}
            title="Script criado internamente pela sua empresa"
          >
            ✦ Personalizado
          </span>
        )}
        {script.criterios.map((c) => (
          <span
            key={c}
            className={`sm-list-tag ${criteriosAtivos.includes(c) ? "sm-list-tag--active" : ""}`}
            onClick={() => adicionarCriterio(c)}
            title="Clique para adicionar como critério"
          >
            {c}
          </span>
        ))}
      </div>

      <div className="sm-card-actions" style={{ flexWrap: "wrap", gap: "6px" }}>
        <button className="sm-btn-copy" style={{ flex: "1 1 auto" }} onClick={() => onAction(textoOriginal, "copy")}>
          Copiar
        </button>
        <button className="sm-btn-whatsapp" style={{ flex: "1 1 auto" }} onClick={() => onAction(textoOriginal, "whatsapp")}>
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
          WhatsApp Web
        </button>
        {temCanalWhatsapp && (
          <button
            style={{
              flex: "1 1 100%",
              background: telefoneCliente ? "#25d366" : "#d1d5db",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: 12,
              fontWeight: 600,
              cursor: telefoneCliente ? "pointer" : "not-allowed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
            }}
            disabled={!telefoneCliente}
            onClick={() => telefoneCliente && onAction(textoOriginal, "envio_rapido", telefoneCliente)}
            title={telefoneCliente ? "Enviar diretamente pelo WhatsApp conectado" : "Oportunidade sem telefone cadastrado"}
          >
            <i className="fab fa-whatsapp" /> Envio Rápido
          </button>
        )}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
const ScriptsModal = ({ isOpen, onClose, oportunidade }) => {
  const { empresaSelecionada } = useContext(EmpresaContext);

  const [scripts, setScripts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [criteriosAtivos, setCriteriosAtivos] = useState([]);
  const [inputCriterio, setInputCriterio] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [reviewAction, setReviewAction] = useState(null);
  const [activeTab, setActiveTab] = useState("kanban");
  const [envioRapido, setEnvioRapido] = useState(null); // { texto, telefone }
  const [envioRapidoLoading, setEnvioRapidoLoading] = useState(false);
  const [envioRapidoErro, setEnvioRapidoErro] = useState("");
  const [envioRapidoSucesso, setEnvioRapidoSucesso] = useState(false);
  const [showConectar, setShowConectar] = useState(false);
  const [temCanalWhatsapp, setTemCanalWhatsapp] = useState(false);
  const [numeroCanal, setNumeroCanal] = useState(null);

  const inputRef = useRef(null);
  const sentinelRef = useRef(null);
  const pageRef = useRef(1);

  // ── Debounce da busca textual ──────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Verifica canal WhatsApp do usuário ao abrir ───────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    api.get("/marketing/me/playnee-conversas/meu-status")
      .then(({ data }) => {
        setTemCanalWhatsapp(!!data.numero);
        setNumeroCanal(data.numero || null);
      })
      .catch(() => {
        setTemCanalWhatsapp(false);
        setNumeroCanal(null);
      });
  }, [isOpen]);

  // ── Reset ao abrir / trocar oportunidade ─────────────────────────────────
  const oportunidadeId = oportunidade?.id;
  useEffect(() => {
    if (!isOpen) return;
    setCriteriosAtivos(extrairCriterios(oportunidade));
    setSearch("");
    setDebouncedSearch("");
    setInputCriterio("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, oportunidadeId]);

  // ── Fetch página 1 sempre que filtros mudam ───────────────────────────────
  const criteriosKey = criteriosAtivos.join(",");
  useEffect(() => {
    if (!isOpen || !empresaSelecionada) return;
    let cancelled = false;

    async function fetchPage1() {
      setLoadingScripts(true);
      setScripts([]);
      setHasMore(false);
      pageRef.current = 1;
      try {
        const params = { page: 1, limit: LIMIT };
        if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
        if (criteriosAtivos.length) params.criterios = criteriosKey;

        const res = await api.get(
          `/common/empresas/${empresaSelecionada.id}/scripts-venda`,
          { params }
        );
        if (cancelled) return;

        const { data, lastPage } = res.data;
        setScripts(formatarScripts(data));
        setHasMore(1 < (lastPage || 1));
      } catch (err) {
        if (!cancelled) console.error("Erro ao carregar scripts", err);
      } finally {
        if (!cancelled) setLoadingScripts(false);
      }
    }

    fetchPage1();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearch, criteriosKey, empresaSelecionada?.id]);

  // ── Carregar próxima página ───────────────────────────────────────────────
  const loadNextPage = useCallback(async () => {
    if (loadingMore || loadingScripts || !hasMore || !empresaSelecionada) return;
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      const params = { page: nextPage, limit: LIMIT };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (criteriosKey) params.criterios = criteriosKey;

      const res = await api.get(
        `/common/empresas/${empresaSelecionada.id}/scripts-venda`,
        { params }
      );
      const { data, lastPage } = res.data;
      setScripts((prev) => [...prev, ...formatarScripts(data)].slice(-MAX_DOM_CARDS));
      pageRef.current = nextPage;
      setHasMore(nextPage < (lastPage || 1));
    } catch (err) {
      console.error("Erro ao carregar mais scripts", err);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loadingScripts, hasMore, debouncedSearch, criteriosKey, empresaSelecionada]);

  // ── IntersectionObserver para scroll infinito ─────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadNextPage(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadNextPage]);

  const criteriosIniciais = useMemo(
    () => extrairCriterios(oportunidade),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [oportunidadeId]
  );

  const removerCriterio = useCallback((c) => {
    setCriteriosAtivos((prev) => prev.filter((x) => x !== c));
  }, []);

  const adicionarCriterio = useCallback((valor) => {
    const norm = normalizar(valor);
    if (!norm) return;
    setCriteriosAtivos((prev) => prev.includes(norm) ? prev : [...prev, norm]);
  }, []);

  const handleCardAction = useCallback(async (texto, type, telefone) => {
    if (type === "envio_rapido") {
      try {
        const { data } = await api.get("/marketing/me/playnee-conversas/meu-status");
        setNumeroCanal(data.numero || null);
        if (!data.connected) {
          setShowConectar(true);
          return;
        }
        setEnvioRapidoErro("");
        setEnvioRapido({ texto, telefone, instancia: data.numero });
      } catch {
        setShowConectar(true);
      }
      return;
    }
    setReviewAction({ texto, type });
  }, []);

  const scriptsComScore = useMemo(() =>
    scripts
      .map((s) => ({ ...s, score: calcularScore(s, criteriosAtivos) }))
      .sort((a, b) => b.score - a.score),
    [scripts, criteriosAtivos]
  );

  const fontesOriginais = useMemo(() => new Set(criteriosIniciais), [criteriosIniciais]);

  const temCriterios = criteriosAtivos.length > 0;

  if (!isOpen) return null;

  function handleInputKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      adicionarCriterio(inputCriterio);
      setInputCriterio("");
    }
  }

  return (
    <>
      <div className="sm-backdrop" onClick={onClose} />
      <div className="sm-drawer" onClick={(e) => e.stopPropagation()}>

          {/* ── Header ── */}
          <div className="sm-header">
            <div>
              <h2 className="sm-title">Scripts de Venda</h2>
              <p className="sm-subtitle">
                {activeTab === "kanban"
                  ? "Scripts recomendados para o perfil da sua empresa, agrupados por situação."
                  : "Scripts filtrados pelos critérios da oportunidade. Ajuste as tags para refinar."}
              </p>
            </div>
            <button className="sm-close" onClick={onClose} aria-label="Fechar">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* ── Tabs ── */}
          <div className="sm-tabs">
            <button
              className={`sm-tab ${activeTab === "kanban" ? "sm-tab--active" : ""}`}
              onClick={() => setActiveTab("kanban")}
            >
              <i className="fas fa-star" />
              Recomendados
            </button>
            <button
              className={`sm-tab ${activeTab === "busca" ? "sm-tab--active" : ""}`}
              onClick={() => setActiveTab("busca")}
            >
              <i className="fas fa-search" />
              Busca avançada
            </button>
          </div>

          {activeTab === "kanban" && (
            <div className="sm-kanban-wrap">
              <ScriptsKanban
                oportunidade={oportunidade}
                onAction={handleCardAction}
                temCanalWhatsapp={temCanalWhatsapp}
              />
            </div>
          )}

          {activeTab === "busca" && (
          <>
          {/* ── Critérios ── */}
          <div className="sm-criteria-section">
            <div className="sm-criteria-top-row">
              <p className="sm-criteria-label">CRITÉRIOS ATIVOS</p>
              <div className="sm-search-wrap">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-search-icon">
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  className="sm-search"
                  placeholder="Pesquisar scripts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="sm-criteria-input-row">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                className="sm-criteria-input"
                placeholder="Adicionar critério..."
                value={inputCriterio}
                onChange={(e) => setInputCriterio(e.target.value)}
                onKeyDown={handleInputKeyDown}
                onBlur={() => {
                  if (inputCriterio.trim()) {
                    adicionarCriterio(inputCriterio);
                    setInputCriterio("");
                  }
                }}
              />
            </div>

            <div className="sm-criteria-tags">
              {criteriosAtivos.map((c) => (
                <span
                  key={c}
                  className={`sm-tag ${fontesOriginais.has(c) ? "sm-tag--origem" : "sm-tag--custom"}`}
                >
                  {c}
                  <button
                    className="sm-tag-remove"
                    onClick={() => removerCriterio(c)}
                    aria-label={`Remover ${c}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {criteriosAtivos.length === 0 && (
                <span className="sm-criteria-empty">Nenhum critério ativo.</span>
              )}
            </div>
          </div>

          {/* ── Lista ── */}
          <div className="sm-all-section">
            <div className="sm-all-header">
              <p className="sm-section-label">
                {temCriterios ? "RECOMENDADOS PARA ESSES CRITÉRIOS" : "TODOS OS SCRIPTS"}
              </p>
            </div>

            <div className="sm-cards-grid">
              {loadingScripts && (
                <p className="sm-list-empty">Carregando scripts...</p>
              )}
              {!loadingScripts && scriptsComScore.length === 0 && (
                <p className="sm-list-empty">Nenhum script encontrado.</p>
              )}
              {!loadingScripts && scriptsComScore.map((script) => (
                <ScriptCard
                  key={script.key}
                  script={script}
                  criteriosAtivos={criteriosAtivos}
                  adicionarCriterio={adicionarCriterio}
                  oportunidade={oportunidade}
                  onAction={handleCardAction}
                  temCanalWhatsapp={temCanalWhatsapp}
                />
              ))}

              {/* Sentinel para scroll infinito */}
              <div ref={sentinelRef} style={{ height: 1 }} />

              {loadingMore && (
                <p className="sm-list-empty" style={{ padding: "8px 0" }}>
                  Carregando mais scripts...
                </p>
              )}
            </div>
          </div>
          </>
          )}

          {/* ── Footer ── */}
          <div className="sm-footer">
            <svg style={{ color: "#d1d5db" }} xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.9 6.9a2.12 2.12 0 0 1-3-3l6.9-6.9a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
            </svg>
            {activeTab === "kanban"
              ? "Visualização kanban por situação. Use a aba Busca avançada para filtros por tags."
              : "Scripts filtrados por IA com base nos critérios ativos."}
          </div>
      </div>

      {reviewAction && (
        <ReviewModal
          initialText={reviewAction.texto}
          type={reviewAction.type}
          onConfirm={() => setReviewAction(null)}
          onCancel={() => setReviewAction(null)}
        />
      )}

      <ConectarPlayneeConversasModal
        isOpen={showConectar}
        onClose={() => setShowConectar(false)}
        onConnected={() => setShowConectar(false)}
        numeroAtual={numeroCanal}
      />

      {envioRapido && (
        <div className="sm-overlay" onClick={() => setEnvioRapido(null)} style={{ zIndex: 2010, backdropFilter: "blur(2px)" }}>
          <div className="sm-modal" style={{ maxWidth: 480, height: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className="sm-header">
              <div>
                <h2 className="sm-title">Envio Rápido</h2>
                <p className="sm-subtitle">
                  Enviar para: <strong>{envioRapido.telefone || "número não disponível"}</strong>
                </p>
              </div>
              <button className="sm-close" onClick={() => setEnvioRapido(null)} aria-label="Fechar">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div style={{ padding: "20px" }}>
              <textarea
                className="sm-card-textarea"
                value={envioRapido.texto}
                onChange={(e) => setEnvioRapido((prev) => ({ ...prev, texto: e.target.value }))}
                rows={6}
                style={{ fontSize: "0.85rem" }}
                autoFocus
                disabled={envioRapidoSucesso}
              />
              {envioRapidoSucesso && (
                <p style={{ color: "#25d366", fontSize: 13, fontWeight: 600, marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="fas fa-check-circle" /> Mensagem enviada com sucesso!
                </p>
              )}
              {envioRapidoErro && !envioRapidoSucesso && (
                <p style={{ color: "#ef4444", fontSize: 12, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <i className="fas fa-exclamation-circle" /> {envioRapidoErro}
                </p>
              )}
            </div>
            <div style={{ padding: "0 20px 20px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button className="sm-btn-reset" style={{ padding: "8px 16px" }} onClick={() => { setEnvioRapido(null); setEnvioRapidoSucesso(false); setEnvioRapidoErro(""); }}>
                {envioRapidoSucesso ? "Fechar" : "Cancelar"}
              </button>
              {!envioRapidoSucesso && (
                <button
                  style={{
                    padding: "8px 16px",
                    background: "#25d366",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: envioRapidoLoading ? "not-allowed" : "pointer",
                    opacity: envioRapidoLoading ? 0.7 : 1,
                  }}
                  disabled={envioRapidoLoading}
                  onClick={async () => {
                    if (!envioRapido.telefone) {
                      setEnvioRapidoErro("Número do cliente não disponível.");
                      return;
                    }
                    setEnvioRapidoLoading(true);
                    setEnvioRapidoErro("");
                    try {
                      await api.post("/marketing/playnee-conversas/enviar", {
                        destinatario: envioRapido.telefone.replace(/\D/g, ""),
                        texto: envioRapido.texto,
                        instancia: envioRapido.instancia,
                      });
                      setEnvioRapidoSucesso(true);
                      setTimeout(() => { setEnvioRapido(null); setEnvioRapidoSucesso(false); }, 2000);
                    } catch (err) {
                      setEnvioRapidoErro(err?.response?.data?.message || "Erro ao enviar mensagem.");
                    } finally {
                      setEnvioRapidoLoading(false);
                    }
                  }}
                >
                  {envioRapidoLoading ? "Enviando..." : <><i className="fab fa-whatsapp mr-1" /> Enviar</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
export const ReviewModal = ({ initialText, type, onConfirm, onCancel }) => {
  const [texto, setTexto] = useState(initialText);
  const [copiado, setCopiado] = useState(false);

  function execute() {
    if (type === "copy") {
      navigator.clipboard.writeText(texto).then(() => {
        setCopiado(true);
        setTimeout(() => {
          setCopiado(false);
          onConfirm(texto);
        }, 800);
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, "_blank");
      onConfirm(texto);
    }
  }

  return (
    <div className="sm-overlay" onClick={onCancel} style={{ zIndex: 2010, backdropFilter: "blur(2px)" }}>
      <div className="sm-modal" style={{ maxWidth: 480, height: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="sm-header">
          <div>
            <h2 className="sm-title">
              {type === "copy" ? "Copiar Script" : "Enviar no WhatsApp"}
            </h2>
            <p className="sm-subtitle">Revise e edite a mensagem antes de enviá-la</p>
          </div>
          <button className="sm-close" onClick={onCancel} aria-label="Fechar">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div style={{ padding: "20px" }}>
          <textarea
            className="sm-card-textarea"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={6}
            style={{ fontSize: "0.85rem" }}
            autoFocus
          />
        </div>
        <div style={{ padding: "0 20px 20px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button className="sm-btn-reset" style={{ padding: "8px 16px" }} onClick={onCancel}>
            Cancelar
          </button>
          <button
            className={type === "copy" ? "sm-btn-copy" : "sm-btn-whatsapp"}
            style={type === "copy" ? { background: "#1d4ed8", color: "#fff", padding: "8px 16px" } : { padding: "8px 16px" }}
            onClick={execute}
          >
            {type === "copy" ? (copiado ? "Copiado!" : "Confirmar e Copiar") : "WhatsApp Web"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScriptsModal;
